import { Box, Divider, Typography } from "@mui/material";
import { useCallback, useReducer } from "react";
import EnhancedTable from "../../../enhancedTable/EnhancedTable";
import { isLocalDev } from "../../../../utils/environment";
import { ValidDataService } from "../../../../service/validDataService";
import useAbortController from "../../../../hooks/useAbortController";
import useDialogModal from "../../../../hooks/useDialogModal";
import ConfirmationDialog from "../../../ui/confirmationDialog";
import { toast } from "react-toastify";
import validLookup from "../../../../utils/validLookup";
import ValidManufacturerInputForm from "./validManufacturerInputForm";
import EditValidManufacturerForm from "./editValidManufacturerForm";
import { validManufacturerColumns } from "./columns";
import EditIcon from "@mui/icons-material/Edit";
import ContentCopy from "@mui/icons-material/ContentCopy";

export default function ValidManufacturer(props) {
    const [ValidManufacturerInputDialog, showValidManufacturerInputForm] = useDialogModal(ValidManufacturerInputForm);
    const [ValidManufacturerEditDialog, showValidManufacturerEditForm] = useDialogModal(EditValidManufacturerForm);

    const [
        ConfirmationDialogComp,
        showConfirmationDialog,
        closeConfirmationDialog,
    ] = useDialogModal(ConfirmationDialog);

    const [signal, abortRequest] = useAbortController();
    const [state, updateState] = useReducer((state, params) => {
        return { ...state, ...params };
    }, {
        data: [],
        totalRowCount: 0,
        pageCount: 0,
        filter: '',
        loading: false,
    });

    const onAddRecordClicked = () => {
        showValidManufacturerInputForm();
    }

    const handleRowEdit = (rowData) => {
        showValidManufacturerEditForm(null, { recordToEdit: rowData });
    }

    const handleRowCopy = (rowData) => {
        showValidManufacturerEditForm(null, { recordToEdit: rowData, isCopy: true });
    }

    /**
    * fetch data
    */
    const fetchData = useCallback(({ pageSize, pageIndex, filters, sortBy }) => {
        const from = pageSize * pageIndex;
        //updateState({ loading: true });
        ValidDataService.getValidManufacturerDataWithLimit(from, pageSize, filters, sortBy, signal).then(result => {
            const { code, data } = result;
            if (code === 0) {
                updateState({
                    data: data.data,
                    totalRowCount: data.count,
                    pageCount: Math.ceil(data.count / pageSize),
                });
            }
            //updateState({ loading: false });
        }).catch(error => {
            if (isLocalDev) {
                console.error(error)
            }
            //updateState({ loading: false });
        });
    }, [signal]);

    const deleteRecordHandler = (rowids) => {
        showConfirmationDialog(async (action) => {
            if (action === validLookup.confirmationDialogAction.ok) {
                const deleteRecordResult = await ValidDataService.deleteValidManufacturer(rowids.map(x => parseInt(x, 10)));
                const { code, message } = deleteRecordResult;

                if (code === 0) {
                    toast.success(message);
                    const updateList = state.data.filter(d => !rowids.map(x => parseInt(x, 10)).includes(d.id));
                    updateState({ data: updateList });
                } else {
                    toast.error("Failed to delete record(s)");
                }
            }
            closeConfirmationDialog();
        }, {
            title: 'Delete record',
            message: `Please confirm if you would like to delete ${rowids.length} record(s)?`,
        });
    }

    const recordAddHandler = (newRecord) => {
        const newData = state.data.concat(newRecord);
        updateState({ data: newData });
    }

    const recordEditHandler = (record) => {
        // find the manufacturer by row id
        const existingManufacturers = state.data;
        const idx = existingManufacturers.findIndex(e => e.id === record.id);
        const updatesList = existingManufacturers.map((row, index) => {
            if (row.id === record.id) {
                return {
                    ...existingManufacturers[idx], ...record,
                }
            }
            return row
        });

        // update list
        updateState({
            data: updatesList
        });
    }

    return (
        <Box>
            <Typography variant="h4">Manufacturers</Typography>
            <Divider sx={{ mb: 2 }} />
            <EnhancedTable
                columns={validManufacturerColumns}
                addRecordHandler={onAddRecordClicked}
                deleteRecordHandler={deleteRecordHandler}
                data={state.data}
                totalRowCount={state.totalRowCount}
                pageCount={state.pageCount}
                fetchData={fetchData}
                loading={state.loading}
                selectionMode="multi"
                actions={[
                    {
                        icon: <EditIcon />,
                        onClick: handleRowEdit
                    },
                    {
                        icon: <ContentCopy />,
                        onClick: handleRowCopy
                    },
                ]}
            />
            <ValidManufacturerInputDialog onRecordAdded={recordAddHandler} />
            <ValidManufacturerEditDialog onRecordEdit={recordEditHandler} />

            <ConfirmationDialogComp
                label={{
                    positive: "Confirm",
                    negative: "Cancel",
                }} />
        </Box>
    )
}
